import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllUsers,
  createUser,
  updateUserPermissions,
  deleteUser,
  getUserRole
} from '../services/authService';
import '../styles/UserManagement.css';

const UserManagement = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'user',
    permissions: {
      call_logs: { view: false, edit: false, delete: false },
      nid_verifications: { view: false, edit: false, delete: false },
      // user_balance: { view: false, edit: false, delete: false },
      recharge_history: { view: false, edit: false, delete: false },
      user_account: { view: false, edit: false, delete: false },
      user_sessions: { view: false, edit: false, delete: false },
      rate_sheets: { view: false, edit: false, delete: false },
      stickers: { view: false, edit: false, delete: false },
      chat_reports: { view: false, edit: false, delete: false },
      account_deletion_requests: { view: false, edit: false, delete: false },
      service_numbers: { view: false, edit: false, delete: false },
    }
  });

  useEffect(() => {
    if (getUserRole() !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await getAllUsers();
      if (res.success) setUsers(res.users || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload =
        newUser.role === 'admin'
          ? { email: newUser.email, password: newUser.password, role: 'admin' }
          : newUser;

      const res = await createUser(payload);
      if (res.success) {
        setShowAddModal(false);
        setNewUser({
          email: '',
          password: '',
          role: 'user',
          permissions: {
            call_logs: { view: false, edit: false, delete: false },
            nid_verifications: { view: false, edit: false, delete: false },
            // user_balance: { view: false, edit: false, delete: false },
            recharge_history: { view: false, edit: false, delete: false },
            user_account: { view: false, edit: false, delete: false },
            user_sessions: { view: false, edit: false, delete: false },
      rate_sheets: { view: false, edit: false, delete: false },
      stickers: { view: false, edit: false, delete: false },
      chat_reports: { view: false, edit: false, delete: false },
      account_deletion_requests: { view: false, edit: false, delete: false },
      service_numbers: { view: false, edit: false, delete: false },
          }
        });
        loadUsers();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    await deleteUser(id);
    loadUsers();
  };

  const handleUpdatePermissions = async () => {
    if (!selectedUser) return;
    await updateUserPermissions(selectedUser.id, selectedUser.permissions);
    setShowPermissionsModal(false);
    setSelectedUser(null);
    loadUsers();
  };

  const togglePermission = (resource, action, isNew = false) => {
    const setter = isNew ? setNewUser : setSelectedUser;
    setter(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [resource]: {
          ...prev.permissions[resource],
          [action]: !prev.permissions[resource][action]
        }
      }
    }));
  };

  // Ensure existing users have the new permission keys when opening modal
  const handleOpenPermissionsModal = (user) => {
    const defaultPerms = {
      call_logs: { view: false, edit: false, delete: false },
      nid_verifications: { view: false, edit: false, delete: false },
      // user_balance: { view: false, edit: false, delete: false },
      recharge_history: { view: false, edit: false, delete: false },
      user_account: { view: false, edit: false, delete: false },
      user_sessions: { view: false, edit: false, delete: false },
      rate_sheets: { view: false, edit: false, delete: false },
      stickers: { view: false, edit: false, delete: false },
      chat_reports: { view: false, edit: false, delete: false },
      account_deletion_requests: { view: false, edit: false, delete: false },
      service_numbers: { view: false, edit: false, delete: false },
    };

    const mergedPermissions = Object.keys(defaultPerms).reduce((acc, key) => {
      acc[key] = {
        ...defaultPerms[key],
        ...(user.permissions?.[key] || {}),
      };
      return acc;
    }, {});

    setSelectedUser({ ...user, permissions: mergedPermissions });
    setShowPermissionsModal(true);
  };

  const PermissionsEditor = ({ permissions, isNew }) => (
    <div className="permissions-box">
      <div className="permissions-header">
        <div>Resource</div>
        <div>View</div>
        <div>Edit</div>
        <div>Delete</div>
      </div>

      {Object.entries(permissions).map(([res, actions]) => (
        <div className="permissions-row" key={res}>
          <div>{res.replace(/_/g, ' ')}</div>
          {['view', 'edit', 'delete'].map(a => (
            <div key={a}>
              <input
                type="checkbox"
                checked={actions[a] || false}
                onChange={() => togglePermission(res, a, isNew)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="user-management-page">
      <div className="user-management-container">

        <div className="page-header">
          <h1 className="page-title">User Management</h1>
          <button className="add-user-button" onClick={() => setShowAddModal(true)}>
            + Add User
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-box">Loading users...</div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`role-badge role-${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      {u.role === 'admin'
                        ? 'All Access'
                        : (
                          <button
                            className="view-button"
                            onClick={() => handleOpenPermissionsModal(u)}
                          >
                            View / Edit
                          </button>
                        )}
                    </td>
                    <td>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteUser(u.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD USER MODAL */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="modal-title">Add User</h2>

            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {newUser.role === 'user' && (
                <PermissionsEditor permissions={newUser.permissions} isNew />
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button className="submit-button">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT PERMISSIONS MODAL */}
      {showPermissionsModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="modal-title">Edit Permissions</h2>
            <p>{selectedUser.email}</p>

            <PermissionsEditor permissions={selectedUser.permissions} />

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setShowPermissionsModal(false)}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleUpdatePermissions}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;